/**
 * Name Input Component
 *
 * Collects first and last name.
 * LoadingBar and navigation buttons are rendered by the parent layout.
 */

"use client";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@frontend/shared/components/ui/form";
import { Input } from "@frontend/shared/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useForm } from "react-hook-form";
import {
  useInsuranceConnectorContext,
  useInsuranceConnectorSend,
  useInsuranceConnectorState,
} from "../insurance-connector-provider";
import {
  type NameFormData,
  nameSchema,
} from "../machine/schemas/validation.schemas";
import { INSURANCE_STEP_FORM_ID } from "../step-config";

export function NameInput() {
  const t = useTranslations("InsuranceConnector");
  const context = useInsuranceConnectorContext();
  const send = useInsuranceConnectorSend();
  const state = useInsuranceConnectorState();

  const stateValue = state.value as { full?: string } | string;
  const fullState = typeof stateValue === "object" ? stateValue.full : "";
  const isRetry = fullState?.includes("unverifiedRetry") ?? false;

  const firstName = isRetry
    ? context.primaryInsurance?.insuredFirstName || context.firstName || ""
    : context.firstName || "";
  const lastName = isRetry
    ? context.primaryInsurance?.insuredLastName || context.lastName || ""
    : context.lastName || "";

  const form = useForm<NameFormData>({
    resolver: zodResolver(nameSchema),
    defaultValues: {
      firstName,
      lastName,
    },
  });

  const onSubmit = (data: NameFormData) => {
    send({ type: "SUBMIT", data });
  };

  const headerText = isRetry ? t("ConfirmNameEdit") : t("WhatsYourName");

  return (
    <Form {...form}>
      <form
        id={INSURANCE_STEP_FORM_ID}
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-4 md:gap-8"
      >
        <h2 className="text-2xl md:text-[32px] leading-[120%] font-semibold">
          {headerText}
        </h2>
        <div className="flex flex-col md:flex-row gap-0 md:gap-8">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel className="sr-only">{t("FirstName")}</FormLabel>
                <FormControl>
                  <Input
                    className="rounded-full py-8 text-lg w-full px-5 border-transparent focus:border-transparent focus:ring-1 focus:ring-[#00A3FF] focus:outline-none focus-visible:ring-1 focus-visible:ring-[#00A3FF] focus-visible:ring-offset-0"
                    placeholder={t("FirstName")}
                    autoFocus
                    autoCapitalize="words"
                    autoCorrect="off"
                    autoComplete="given-name"
                    spellCheck={false}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel className="sr-only">{t("LastName")}</FormLabel>
                <FormControl>
                  <Input
                    className="rounded-full py-8 text-lg w-full px-5 border-transparent focus:border-transparent focus:ring-1 focus:ring-[#00A3FF] focus:outline-none focus-visible:ring-1 focus-visible:ring-[#00A3FF] focus-visible:ring-offset-0"
                    placeholder={t("LastName")}
                    autoCapitalize="words"
                    autoCorrect="off"
                    autoComplete="family-name"
                    spellCheck={false}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </form>
    </Form>
  );
}
